import { useMemo } from 'react';

import {
  EncounterSchema,
  MedicalProvider,
  ProviderTreatmentStatus,
} from '../../../services/types/treatment-types';
import { getSimpleTreatmentStatus, SimpleTreatmentStatus } from '../utils';
import { TimelineEncounter } from './TimelineEncounter';
import {
  calculateDatePosition,
  findTreatmentGaps,
  isValidDate,
  parseDate,
} from './utils';

interface TimelineLaneProps {
  provider: MedicalProvider;
  encounters: EncounterSchema[];
  startDate: Date; // Now refers to visibleStartDate
  endDate: Date; // Now refers to visibleEndDate
  selectedEncounterId: string | null;
  onSelectEncounter: (encounterId: string) => void;
  showGaps: boolean;
  gapThresholdDays: number;
}

// Used to prevent encounters that are too close to each other from overlapping
const MIN_ENCOUNTER_SEPARATION_PERCENT = 2;

const TreatmentStatus = ({
  treatmentStatus,
}: {
  treatmentStatus: ProviderTreatmentStatus;
}) => {
  const currentStatus = getSimpleTreatmentStatus(treatmentStatus);
  if (currentStatus === SimpleTreatmentStatus.TREATING) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-normal bg-emerald-50 text-emerald-700 ml-2 border border-emerald-100">
        Active
      </span>
    );
  }

  if (currentStatus === SimpleTreatmentStatus.COMPLETED) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-normal bg-gray-50 text-gray-600 ml-2 border border-gray-100">
        Completed
      </span>
    );
  }

  return null;
};

export const TimelineLane = ({
  provider,
  encounters,
  startDate,
  endDate,
  selectedEncounterId,
  onSelectEncounter,
  showGaps,
  gapThresholdDays,
}: TimelineLaneProps) => {
  const treatmentGaps = useMemo(() => {
    if (!showGaps) return [];
    return findTreatmentGaps(encounters, gapThresholdDays);
  }, [encounters, showGaps, gapThresholdDays]);

  // Calculate positions and handle collisions for encounters that are too close together
  const positionedEncounters = useMemo(() => {
    // First, calculate base positions
    const basePositioned = encounters
      .filter((encounter) => encounter.date && isValidDate(encounter.date))
      .map((encounter) => {
        const date = parseDate(encounter.date as string);
        const position = calculateDatePosition(date, startDate, endDate);

        return {
          encounter: {
            ...encounter,
            providerId: provider.id,
            providerName: provider.name || undefined,
          },
          position,
          dateObj: date,
        };
      })
      .sort((a, b) => a.position - b.position); // Sort by position

    // Then adjust positions for encounters that are too close together
    const adjustedPositions = [...basePositioned];
    for (let i = 1; i < adjustedPositions.length; i += 1) {
      const current = adjustedPositions[i];
      const previous = adjustedPositions[i - 1];

      // If encounters are too close
      if (
        current.position - previous.position <
        MIN_ENCOUNTER_SEPARATION_PERCENT
      ) {
        // Only visible encounters need adjustment (within 0-100% range)
        if (
          current.position >= 0 &&
          current.position <= 100 &&
          previous.position >= 0 &&
          previous.position <= 100
        ) {
          // Move later encounters slightly to the right
          const newPosition =
            previous.position + MIN_ENCOUNTER_SEPARATION_PERCENT;
          if (newPosition <= 100) {
            // Only adjust if it stays in visible range
            adjustedPositions[i] = { ...current, position: newPosition };
          }
        }
      }
    }

    return adjustedPositions;
  }, [encounters, startDate, endDate, provider.id, provider.name]);

  const positionedGaps = useMemo(() => {
    return treatmentGaps.map((gap) => {
      // Use visible start/end date for positioning gaps
      const startPosition = calculateDatePosition(
        gap.start,
        startDate,
        endDate,
      );
      const endPosition = calculateDatePosition(gap.end, startDate, endDate);

      return {
        start: startPosition,
        end: endPosition,
        width: Math.max(0, endPosition - startPosition), // Ensure width is not negative
        days: gap.days,
      };
    });
  }, [treatmentGaps, startDate, endDate]);

  // Determine if this lane has a selected encounter
  const hasSelectedEncounter =
    selectedEncounterId && encounters.some((e) => e.id === selectedEncounterId);

  return (
    <div
      className={`relative h-16 border-b border-gray-100 group flex transition-colors duration-200
        ${hasSelectedEncounter ? 'bg-blue-50/40' : ''}`}
    >
      {/* Provider name column (fixed width) */}
      <div
        className={`sticky left-0 w-52 bg-white z-20 border-r border-gray-100 flex items-center px-3 
          transition-colors flex-shrink-0
          ${hasSelectedEncounter ? 'bg-blue-50' : 'group-hover:bg-gray-50'}`}
      >
        <div className="truncate">
          <div className="text-sm font-medium text-gray-800 truncate">
            {provider.name || 'Unnamed Provider'}
            <TreatmentStatus treatmentStatus={provider.treatment_status} />
          </div>
          {provider.encounters && (
            <div className="text-xs text-gray-500 flex items-center">
              <span className="h-1.5 w-1.5 rounded-full bg-gray-300 mr-1.5" />
              {provider.encounters.length} encounter
              {provider.encounters.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {/* Timeline events area (takes remaining width) */}
      <div
        className={`relative flex-grow transition-all duration-200 ease-in-out
          ${hasSelectedEncounter ? 'bg-blue-50/40' : 'bg-gray-50 group-hover:bg-gray-100/30'}`}
      >
        {/* Timeline lane centerline */}
        <div className="absolute top-1/2 left-0 right-0 h-px bg-gray-200/70 transform -translate-y-1/2" />

        {/* Treatment gaps */}
        {showGaps &&
          positionedGaps.map(
            (gap, index) =>
              gap.width > 0.1 && ( // Only render if gap has a meaningful width
                <div
                  key={`gap-${index}-${provider.id}`}
                  className="absolute top-0 h-full bg-red-50/80 border-l border-r border-red-200/70 z-0"
                  style={{
                    left: `${gap.start}%`,
                    width: `${gap.width}%`,
                    backgroundImage:
                      'repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(220, 38, 38, 0.05) 8px, rgba(220, 38, 38, 0.05) 16px)',
                    boxShadow: 'inset 0 0 12px rgba(220, 38, 38, 0.03)',
                  }}
                  title={`${gap.days} day gap in treatment`}
                >
                  {gap.width > 3 && (
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[10px] font-medium text-red-600 whitespace-nowrap px-1.5 py-0.5 bg-white/90 rounded shadow-sm border border-red-100">
                      {gap.days}d gap
                    </div>
                  )}
                </div>
              ),
          )}

        {/* Encounters - only render those in visible range */}
        {positionedEncounters
          .filter(({ position }) => position >= -1 && position <= 101) // Slight buffer for partial visibility
          .map(({ encounter, position }) => (
            <TimelineEncounter
              key={`${encounter.id}-${provider.id}`}
              encounter={encounter}
              position={position}
              isSelected={selectedEncounterId === encounter.id}
              onSelect={onSelectEncounter}
            />
          ))}
      </div>
    </div>
  );
};
